import type { OperationItem } from '@travel-planner/shared/schemas/operations';
import { format } from 'date-fns';
import { toUtcDate } from './office-time';

// Pure field resolution for the hotel supplier-reservation email (XT-600).
// Every value comes off the item payload Operations already loads — the
// template adds no API surface. Kept free of React so each mapping rule in the
// spec is unit-testable on its own.

type RoomPrice = {
  roomTypeId?: string | null;
  roomTitle?: string | null;
  // Pre-V2 rows stored the label inline under `roomType`.
  roomType?: string | null;
  cost?: number | null;
  netCost?: number | null;
};

/**
 * Whether the org has switched the hotel template on for a given country.
 * Off unless the country is explicitly listed, so an org opts in per desk.
 * Callers pass the ITINERARY's country of travel — a trip belongs to one desk,
 * while its services may sit in several countries. Matched case-insensitively
 * on the trimmed name because the setting is seeded from CMS landing slugs
 * (`landing/morocco`) while the column stores display names ("Morocco").
 */
export const isHotelReservationEmailEnabled = (
  enabledCountries: readonly string[] | null | undefined,
  country: string | null | undefined
): boolean => {
  if (!(enabledCountries && country)) return false;
  const target = country.trim().toLowerCase();
  if (!target) return false;
  return enabledCountries.some((c) => c.trim().toLowerCase() === target);
};

// UTC midnight of a timestamp — the basis for the night count. Built from the
// UTC parts rather than `differenceInCalendarDays`, which reads local-time
// fields and would shift the count either side of midnight.
const utcMidnight = (iso: string): number => {
  const d = toUtcDate(iso);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/** Check-out date minus check-in date, in nights. `null` when unresolvable. */
export const reservationNights = (
  checkIn: string | null,
  checkOut: string | null
): string | null => {
  if (!(checkIn && checkOut)) return null;
  const nights = Math.round(
    (utcMidnight(checkOut) - utcMidnight(checkIn)) / 86_400_000
  );
  if (nights <= 0) return null;
  return `${nights} ${nights === 1 ? 'night' : 'nights'}`;
};

/**
 * "Ddd, D Mmm YYYY, HH:MM", dropping the time when it isn't set. The column is
 * a bare timestamp with no "time known" flag, so midnight is read as unset —
 * a real hotel check-in/out always carries an hour.
 */
export const reservationDateTime = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = toUtcDate(iso);
  const date = format(d, 'EEE, d MMM yyyy');
  if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0) return date;
  return `${date}, ${format(d, 'HH:mm')}`;
};

const roomsOf = (price: unknown): RoomPrice[] => {
  if (price == null || typeof price !== 'object') return [];
  const rooms = (price as { rooms?: unknown }).rooms;
  return Array.isArray(rooms) ? (rooms as RoomPrice[]) : [];
};

const roomLabel = (
  room: RoomPrice,
  titleByRoomTypeId: ReadonlyMap<string, string>
): string | null => {
  // The org catalogue title wins — `roomTitle`/`roomType` are denormalised
  // copies that go stale when a room type is renamed.
  const fromCatalogue = room.roomTypeId
    ? titleByRoomTypeId.get(room.roomTypeId)
    : undefined;
  return (
    fromCatalogue?.trim() ||
    room.roomTitle?.trim() ||
    room.roomType?.trim() ||
    null
  );
};

export type ReservationRoomLine = {
  label: string;
  /** Net (supplier-side) cost for this room line; null when unpriced. */
  netCost: number | null;
};

/**
 * One line per room the service holds, so a multi-room booking is fully
 * accounted for rather than collapsing to the first category.
 */
export const reservationRoomLines = (
  price: unknown,
  titleByRoomTypeId: ReadonlyMap<string, string>
): ReservationRoomLine[] =>
  roomsOf(price).map((room) => ({
    label: roomLabel(room, titleByRoomTypeId) ?? 'Room',
    netCost: room.netCost ?? room.cost ?? null,
  }));

/**
 * Net rate in the SUPPLIER's currency (`item.currency`), never the itinerary's
 * selling currency — the supplier quotes and invoices in their own.
 */
export const formatNetRate = (
  netCost: number | null,
  currency: string | null
): string | null => {
  if (netCost == null || !Number.isFinite(netCost)) return null;
  const amount = netCost.toFixed(2);
  return currency ? `${amount} ${currency}` : amount;
};

const stripHtml = (value: string): string =>
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// contactInfo entries are "Name:email:phone" strings (legacy parity).
const contactNames = (contactInfo: unknown): string[] => {
  if (!Array.isArray(contactInfo)) return [];
  return contactInfo
    .filter((e): e is string => typeof e === 'string' && e.trim().length > 0)
    .map((e) => e.split(':')[0]?.trim() ?? '')
    .filter((n) => n.length > 0);
};

/**
 * "2 Adults - Elsa Schuster, Mariela Luquin" — pax count first, then the
 * traveller-contact names in booking order. Falls back to the count alone when
 * no contacts are recorded.
 */
export const reservationTravellers = (
  countLabel: string | null,
  contactInfo: unknown
): string | null => {
  const names = contactNames(contactInfo);
  if (!countLabel) return names.length > 0 ? names.join(', ') : null;
  return names.length > 0 ? `${countLabel} - ${names.join(', ')}` : countLabel;
};

/**
 * Supplier-facing notes only: the itinerary's "All supplier notes" when set,
 * otherwise each resolved supplier's CRM note. Internal-only notes
 * (`operationInternalNote`, driver notes) are deliberately never included —
 * this text is pasted into a message to the supplier.
 */
export const reservationNotes = (
  allSuppliersNotes: string | null | undefined,
  suppliers: ReadonlyArray<{ crmInfo?: unknown }> | null | undefined
): string | null => {
  const all = allSuppliersNotes ? stripHtml(allSuppliersNotes) : '';
  if (all) return all;
  const notes = (suppliers ?? [])
    .map((s) => (s.crmInfo as { notes?: unknown } | null | undefined)?.notes)
    .filter((n): n is string => typeof n === 'string')
    .map(stripHtml)
    .filter((n) => n.length > 0);
  return notes.length > 0 ? notes.join('; ') : null;
};

/**
 * Fields the spec requires before the request can be sent. A missing one keeps
 * the preview open (the agent needs to see what's wrong) but blocks the copy.
 * An absent net rate is deliberately NOT here — it warns, it doesn't block.
 */
export const missingReservationFields = (args: {
  supplierName: string | null;
  ourReference: string | null;
  checkIn: string | null;
  checkOut: string | null;
}): string[] => {
  const missing: string[] = [];
  if (!args.supplierName?.trim()) missing.push('Supplier');
  if (!args.ourReference?.trim()) missing.push('Our reference');
  if (!args.checkIn) missing.push('Check-in');
  if (!args.checkOut) missing.push('Check-out');
  return missing;
};

export type HotelReservationItem = Pick<
  OperationItem,
  | 'allSuppliersNotes'
  | 'currency'
  | 'endTime'
  | 'itineraryAccountingReference'
  | 'itineraryTravellersContactInfo'
  | 'price'
  | 'startTime'
>;
